import type { EntityType } from '../types/common.js';
import type { AccountHistoryChange, AccountHistoryEntry, WithheldReason } from '../types/accountHistory.js';
import type { Currency } from '../types/account.js';
import { centsToDisplay } from './money.js';

// Account History tab — the PURE presentation logic. Every sentence the tab
// renders comes from here, so each is asserted without jsdom.

// ⚠️⚠️ THE TWO HORIZONS. auditLog begins 26 Apr 2026, and attached entities
// (contacts, portal users, reports, calendars, integrations) are only linked to
// an account from 29 Sep 2026 — no backfill, by decision. So the feed has two
// start dates, and without this line an AM scrolling back past 29 Sep sees
// account edits continuing and contact changes simply stopping, which reads as
// "no contacts changed", not "we weren't linking them yet". Absence read as a
// value. The line is the thing that stops the feed lying; it is asserted.
export const ACCOUNT_RECORD_HISTORY_FROM = '2026-04-26';
export const ATTACHED_HISTORY_FROM = '2026-09-29';

function longDate(ymd: string): string {
  const [y, m, d] = ymd.split('-').map(Number);
  const month = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'][m! - 1];
  return `${d} ${month} ${y}`;
}

export function historyHorizonLine(): string {
  return (
    `Account details, budget and services are shown from ${longDate(ACCOUNT_RECORD_HISTORY_FROM)}. ` +
    `Contacts, portal users, reports, calendars and integrations from ${longDate(ATTACHED_HISTORY_FROM)}.`
  );
}

// ---------- entry wording ----------

const ENTITY_LABELS: Partial<Record<EntityType, string>> = {
  account: 'Account',
  franchiseProfile: 'Franchise profile',
  franchiseLocationProfile: 'Location profile',
  botKnowledgeBase: 'Lead bot knowledge base',
  businessProfile: 'Business profile',
  businessPromotion: 'Promotion',
  contact: 'Contact',
  portalUser: 'Portal user',
  accountOnboarding: 'Meta onboarding',
  hostedReport: 'Hosted report',
  bookingCalendar: 'Booking calendar',
  bookingCalendarMember: 'Booking calendar staff member',
  botConfig: 'Lead bot',
  botTesterInvite: 'Lead bot tester invite',
  vehicleListingUpload: 'Stock upload',
  clubwiseUpload: 'ClubWise upload',
  xeroAllocationRule: 'Xero allocation rule',
  advertisingAlertAck: 'Advertising alert acknowledgement',
  advertisingAlertNote: 'Advertising alert note',
  reportingBackfill: 'Reporting backfill',
  savedAudience: 'Saved audience',
};

export function entityLabel(type: EntityType): string {
  return ENTITY_LABELS[type] ?? type;
}

const VERBS = { create: 'created', update: 'updated', delete: 'deleted' } as const;

export function entrySummary(entry: AccountHistoryEntry): string {
  const verb = VERBS[entry.operation as keyof typeof VERBS] ?? entry.operation;
  return `${entityLabel(entry.entityType)} ${verb}`;
}

const WITHHELD_TEXT: Record<WithheldReason, string> = {
  'not-on-page': 'value not shown here — it is not shown on its own page either',
  'admin-only': 'value visible to admins only',
  'entity-deleted': 'value not shown — this has since been deleted',
};

export function withheldText(reason: WithheldReason): string {
  return WITHHELD_TEXT[reason];
}

const MAX_VALUE_CHARS = 140;

// A readable rendering of one stored value. Never throws.
export function formatHistoryValue(value: unknown): string {
  if (value === null || value === undefined || value === '') return '—';
  if (typeof value === 'boolean') return value ? 'Yes' : 'No';
  if (typeof value === 'number') return String(value);
  if (typeof value === 'string') return clip(value);
  if (Array.isArray(value) && value.length === 0) return '—';
  // Firestore Timestamps arrive serialised as ISO strings by sendJson; an
  // object here is a nested field (a link list, a config block).
  try {
    return clip(JSON.stringify(value));
  } catch {
    return '(unreadable)';
  }
}

// ⚠️⚠️ MONEY IS AN EXPLICIT NAMED LIST, never a heuristic (a name suffix, "a
// large integer"). A field not listed here renders exactly as any other value,
// so a quota or a count can never be divided by 100 by accident. Add a field
// only once BOTH are confirmed: (1) it is stored as integer cents, AND (2) its
// value is in the ACCOUNT'S OWN currency — the formatter labels every amount
// with the account's currency, so a cents field held in some other currency
// would render as a confident, wrongly-labelled figure.
export const MONEY_HISTORY_FIELDS: ReadonlySet<string> = new Set([
  'metaMasterMonthlyBudget',
  'googleMasterMonthlyBudget',
]);

// One value for one field. A listed money field that holds a NUMBER goes to the
// one money formatter; anything else — including null, which must stay '—' and
// never become $0.00 — falls through to formatHistoryValue unchanged.
export function formatHistoryFieldValue(field: string, value: unknown, currency: Currency | null): string {
  if (MONEY_HISTORY_FIELDS.has(field) && typeof value === 'number' && Number.isFinite(value)) {
    return centsToDisplay(value, currency);
  }
  return formatHistoryValue(value);
}

function clip(s: string): string {
  return s.length > MAX_VALUE_CHARS ? `${s.slice(0, MAX_VALUE_CHARS - 1)}…` : s;
}

function isEmpty(v: unknown): boolean {
  return v === null || v === undefined || v === '' || (Array.isArray(v) && v.length === 0);
}

// The changes worth a line. A create records every field of the new doc, most
// of them empty, so an empty field on a create is dropped (it says nothing);
// on an update or delete every change is kept.
export function changesToShow(entry: AccountHistoryEntry): AccountHistoryChange[] {
  if (entry.operation !== 'create') return entry.changes;
  return entry.changes.filter((c) => c.withheld !== undefined || !isEmpty(c.after));
}

// `currency` is the account's CURRENT currency (see AccountHistorySection):
// a budget changed under a different currency renders with today's symbol.
export function describeChange(
  c: AccountHistoryChange,
  operation: AccountHistoryEntry['operation'],
  currency: Currency | null,
): string {
  if (c.withheld) return `${c.field}: ${withheldText(c.withheld)}`;
  const fmt = (v: unknown) => formatHistoryFieldValue(c.field, v, currency);
  if (operation === 'create') return `${c.field}: ${fmt(c.after)}`;
  if (operation === 'delete') return `${c.field} was ${fmt(c.before)}`;
  return `${c.field}: ${fmt(c.before)} → ${fmt(c.after)}`;
}
